import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { RepositoryProvider } from '@/repositories/RepositoryProvider';
import { PlatformTenantProvider } from '@/contexts/PlatformTenantContext';
import { OrganizationProvider } from '@/contexts/OrganizationContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { supabase } from '@/integrations/supabase/client';
import { CMMSRoutes } from './routes';

// Import global styles from monorepo root
import '@/index.css';

// SSO handoff: restore session from query param BEFORE React mounts.
// This prevents React Router redirects from stripping the param.
async function handleSsoHandoff() {
  const urlParams = new URLSearchParams(window.location.search);
  const ssoPayload = urlParams.get('sso_handoff');
  if (!ssoPayload) return;

  try {
    const { access_token, refresh_token } = JSON.parse(atob(ssoPayload));
    if (access_token && refresh_token) {
      urlParams.delete('sso_handoff');
      const cleanSearch = urlParams.toString();
      const cleanUrl = window.location.pathname + (cleanSearch ? '?' + cleanSearch : '');
      window.history.replaceState(null, '', cleanUrl);
      await supabase.auth.setSession({ access_token, refresh_token });
    }
  } catch (e) {
    console.warn('[SSO Handoff] Failed:', e);
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

// Wait for SSO handoff before mounting React
handleSsoHandoff().then(() => {
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <RepositoryProvider>
          <AuthProvider>
            <PlatformTenantProvider>
              <OrganizationProvider>
                <ToastProvider>
                  <BrowserRouter>
                    <CMMSRoutes />
                  </BrowserRouter>
                </ToastProvider>
              </OrganizationProvider>
            </PlatformTenantProvider>
          </AuthProvider>
        </RepositoryProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  </StrictMode>,
);
});
